import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, Download, RefreshCw, Loader2, Filter } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api from "@/lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

export default function StockSignals() {
  const [region, setRegion] = useState("all");
  const [safetyMargin, setSafetyMargin] = useState("0.2");

  const { data: regionsData } = useQuery({
    queryKey: ["admin-regions"],
    queryFn: () => api.get("/api/admin/regions").then((r) => r.data),
  });

  const params = new URLSearchParams({ safety_margin: safetyMargin });
  if (region && region !== "all") params.set("region", region);

  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ["stock-signals", region, safetyMargin],
    queryFn: () => api.get(`/api/admin/stock-signals?${params}`).then((r) => r.data),
  });

  const signals = data?.signals || [];
  const regions = regionsData?.regions || [];

  function handleExport() {
    const ep = params.toString();
    window.open(`${BACKEND}/api/admin/stock-signals/export?${ep}`, "_blank");
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-graphite">Stock Signals</h1>
          <p className="text-steel text-sm mt-1">
            SKUs where warehouse stock is below expected 2-week demand + safety margin. Sorted by value at risk.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            className="flex items-center gap-1.5 border border-border rounded-lg px-3 py-2 text-sm hover:bg-surface"
          >
            <RefreshCw className={`w-4 h-4 ${isFetching ? "animate-spin" : ""}`} /> Refresh
          </button>
          <button
            onClick={handleExport}
            className="flex items-center gap-1.5 bg-graphite text-white rounded-lg px-3 py-2 text-sm hover:bg-orange transition-colors"
          >
            <Download className="w-4 h-4" /> Export Excel
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-steel-light" />
          <span className="text-sm text-steel font-medium">Region</span>
          <Select value={region} onValueChange={setRegion}>
            <SelectTrigger className="w-36 h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All regions</SelectItem>
              {regions.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-steel font-medium">Safety margin</span>
          <Select value={safetyMargin} onValueChange={setSafetyMargin}>
            <SelectTrigger className="w-28 h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="0.1">10%</SelectItem>
              <SelectItem value="0.2">20% (default)</SelectItem>
              <SelectItem value="0.3">30%</SelectItem>
              <SelectItem value="0.5">50%</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Summary */}
      {data && (
        <div className="grid grid-cols-3 gap-4">
          <Card>
            <CardContent className="pt-4">
              <p className="text-sm text-steel">Flagged SKUs</p>
              <p className="text-3xl font-bold text-graphite">{signals.length}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <p className="text-sm text-steel">Total value at risk</p>
              <p className="text-3xl font-bold text-orange">
                ${signals.reduce((s, x) => s + x.value_at_risk, 0).toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <p className="text-sm text-steel">Out of stock</p>
              <p className="text-3xl font-bold text-red-500">{signals.filter((s) => !s.is_in_stock).length}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Table */}
      {isLoading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-steel-light" /></div>
      ) : signals.length === 0 ? (
        <div className="text-center py-16 text-steel">
          <AlertTriangle className="w-10 h-10 mx-auto mb-2 text-steel-light" />
          <p className="font-medium">No stock signals — everything looks fine!</p>
          <p className="text-sm mt-1">Either stock is sufficient or no demand data exists yet.</p>
        </div>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-border bg-surface">
                  <tr>
                    <th className="text-left px-4 py-3 text-steel font-medium">SKU / Name</th>
                    <th className="text-right px-4 py-3 text-steel font-medium">Stock</th>
                    <th className="text-right px-4 py-3 text-steel font-medium">Expected 2w</th>
                    <th className="text-right px-4 py-3 text-steel font-medium">Threshold</th>
                    <th className="text-right px-4 py-3 text-steel font-medium">Shortage</th>
                    <th className="text-right px-4 py-3 text-steel font-medium">Value at Risk</th>
                    <th className="px-4 py-3" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {signals.map((s) => (
                    <tr key={s.sku} className="hover:bg-surface">
                      <td className="px-4 py-3">
                        <p className="font-medium text-graphite">{s.name}</p>
                        <p className="text-xs text-steel">{s.sku} · {s.brand}</p>
                      </td>
                      <td className="px-4 py-3 text-right font-mono">{s.stock_qty}</td>
                      <td className="px-4 py-3 text-right font-mono">{s.expected_2w_qty}</td>
                      <td className="px-4 py-3 text-right font-mono text-orange">{s.threshold_qty}</td>
                      <td className="px-4 py-3 text-right font-mono font-bold text-red-600">{s.shortage}</td>
                      <td className="px-4 py-3 text-right font-bold text-graphite">
                        ${s.value_at_risk.toLocaleString("en-CA", { minimumFractionDigits: 2 })}
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant={s.is_in_stock ? "outline" : "destructive"}>
                          {s.is_in_stock ? "In Stock" : "Out"}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      <p className="text-xs text-steel">
        Stock quantities shown are from the product catalogue. Real-time NetSuite warehouse quantities will replace these heuristics once the integration is live.
      </p>
    </div>
  );
}
